/* Copyright (c) 2023-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Directive, AfterViewInit, ElementRef, OnInit, OnDestroy, Input } from '@angular/core';
import { MatRadioButton } from '@angular/material/radio';
import { FocusMonitor } from '@angular/cdk/a11y';
import { MatSidenav } from '@angular/material/sidenav';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatMenuTrigger } from '@angular/material/menu';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { InputModalityService } from 'input-modality/lib/input-modality.service';
import { NvFocusTrap, NvFocusTrapFactory } from '../services/focus-trap';
import { SpatialNavigationService } from '../services/spatial-navigation/spatial-navigation.service';

@Directive({
    selector: 'mat-radio-button[nvMatRadioSpatialNavigationFixup]',
    standalone: true
})
export class NvMatRadioButtonSpatialNavigationFixupDirective implements AfterViewInit {
    constructor(
        private radio: MatRadioButton,
        private spatialNavigationService: SpatialNavigationService
    ) {}
    ngAfterViewInit(): void {
        if (!this.spatialNavigationService.enabled) {
            return;
        }

        // this override is to address bug 4058561
        // angular material internally updates the radio buttno's tabindex when the selected radio button changes
        // since this tabindex update is done internally, we have to instrument the call to setAttribute to avoid this
        // update. Attach the angular material's source code for future reference.
        // https://github.com/angular/components/blob/13.3.x/src/material/radio/radio.ts#L643
        //
        // FIXME: this is a short-term solution, we have to discuss with UX whether to honor material's default behavior
        if (this.radio._inputElement.nativeElement) {
            const setAttribute = this.radio._inputElement.nativeElement.setAttribute.bind(
                this.radio._inputElement.nativeElement
            );
            this.radio._inputElement.nativeElement.setAttribute = (key, value) => {
                if (key === 'tabindex') {
                    setAttribute(key, 0);
                } else {
                    setAttribute(key, value);
                }
            };
        }
    }
}

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'mat-checkbox',
    standalone: true
})
export class CheckboxFocusMonitorDirective implements OnInit, OnDestroy {
    constructor(
        private elementRef: ElementRef,
        private focusMonitor: FocusMonitor
    ) {}

    ngOnInit() {
        this.focusMonitor.monitor(this.elementRef.nativeElement, true);
    }

    ngOnDestroy() {
        this.focusMonitor.stopMonitoring(this.elementRef.nativeElement);
    }
}

@Directive({
    selector: 'mat-sidenav[nvMatSideNavSpatialNavigationFixup]',
    standalone: true
})
export class NvMatSidenavSpatialNavigationFixupDirective implements AfterViewInit {
    @Input('nvMatSideNavSpatialNavigationFixup') set enabled(val: any) {
        this._enabled = coerceBooleanProperty(val);
    }
    get enabled() {
        return this._enabled;
    }
    private _enabled = false;

    constructor(
        private matsidenav: MatSidenav,
        private spatialNavigationService: SpatialNavigationService
    ) {}
    ngAfterViewInit(): void {
        if ((!this.enabled && !this.spatialNavigationService.enabled) || !this.matsidenav) {
            return;
        }
        const toggle = this.matsidenav.toggle.bind(this.matsidenav);
        // FIXME: AVENGERS-2930
        // this is a known issue tracked in https://github.com/angular/components/issues/29545
        // the fix has already been included into version 18
        // we should remove this WAR once we upgrade to material 18 or the fix is cherry-picked
        // to 16 or 17
        this.matsidenav.toggle = (opened: boolean) => {
            if (this.matsidenav?._container && !opened && this.matsidenav._container.hasBackdrop) {
                this.matsidenav._container.hasBackdrop = false;
            }
            return toggle(opened);
        };
    }
}

@Directive({
    selector: '[nvMatMenuTriggerSpatialNavigationFixup], *[matMenuTriggerFor]',
    standalone: true
})
export class NvMatMenuTriggerSpatialNavigationFixupDirective implements AfterViewInit, OnDestroy {
    private focusTrap?: NvFocusTrap;
    private destroyed$ = new Subject<void>();

    constructor(
        private matMenuTrigger: MatMenuTrigger,
        private spatialNavigationService: SpatialNavigationService,
        private nvFocusTrapFactory: NvFocusTrapFactory,
        private inputModality: InputModalityService
    ) {}

    ngAfterViewInit(): void {
        if (!this.spatialNavigationService.enabled) {
            return;
        }
        this.matMenuTrigger.menuOpened.pipe(takeUntil(this.destroyed$)).subscribe(() => {
            // neither mat-menu-trigger nor mat-menu provides direct access to the actual element
            // mounted in the DOM. We have to use the panelId provided to query the actual element
            const panel = document.getElementById(this.matMenuTrigger.menu.panelId);
            // WAR: mat-menu internally manages the focus on its own. we couldn't use FocusManager to
            // restore focus because it doesn't make mat-menu update its internal state. We have to use
            // mat-menu's focusFirstItem to focus the first item in the dropdown. However, focusFirstItem
            // doesn't move the focus if the focus is in the panel. We have to install the focus trap
            // on the panel's parent which is the overlay container to work around this problem
            this.focusTrap = this.nvFocusTrapFactory.create(panel.parentElement);
            this.focusTrap.attach();
            this.matMenuTrigger.menu.focusFirstItem(this.inputModality.lastFocusOrigin || 'keyboard');
        });

        this.matMenuTrigger.menuClosed.pipe(takeUntil(this.destroyed$)).subscribe(() => {
            this.focusTrap?.detach();
        });
    }

    ngOnDestroy(): void {
        this.focusTrap?.detach();
        this.destroyed$.next();
        this.destroyed$.complete();
    }
}
